import { useEffect, useRef, useCallback, useState } from "react";
import { Toast } from "./Toast";
import moment from "moment";

const INACTIVITY_LIMIT = 2 * 60 * 1000; // 30 minute
const LOGOUT_TIME_LIMIT = 10;
const CHECK_INTERVAL = 1000; // 1 second
const WARNING_TIME = 10 * 1000; // Warning at 10s

const AppLogout = ({ children }) => {
  const [token, setToken] = useState(
    localStorage.getItem("token") || sessionStorage.getItem("token"),
  );
  const lastActivityRef = useRef(moment());
  const isLoggingOutRef = useRef(false);
  const [remainingTime, setRemainingTime] = useState(INACTIVITY_LIMIT);
  const [showWarning, setShowWarning] = useState(false);

  // Check if user is on auth pages
  const isAuthPage = useCallback(() => {
    const hash = window.location.hash.toLowerCase();
    const path = hash.replace("#/", "").split("?")[0];
    const authPaths = ["", "login", "register", "forgot", "create-password"];
    return authPaths.includes(path);
  }, []);

  // Logout action
  const logoutAction = useCallback(
    (reason = "Please re-login to access") => {
      console.log(`[AppLogout] 🚪 Logout initiated. Reason: ${reason}`);
      if (isLoggingOutRef.current || isAuthPage()) {
        console.log("[AppLogout] 🚫 Logout skipped (Already logging out or on auth page)");
        return;
      }

      console.log("[AppLogout] ⚙️ Executing logout: Clearing session and storage.");
      isLoggingOutRef.current = true;
      setShowWarning(false);
      Toast(reason);

      sessionStorage.clear();
      localStorage.removeItem("token");
      localStorage.removeItem("last_active");
      localStorage.removeItem("login_time");
      setToken(null);

      console.log("[AppLogout] 🧭 Redirecting to login and refreshing page.");
      window.location.href = "/#/";
      setTimeout(() => window.location.reload(), 100);
    },
    [isAuthPage],
  );

  // Reset activity
  const resetActivity = useCallback(() => {
    if (isLoggingOutRef.current || isAuthPage() || !token) return;

    console.log("[AppLogout] ⚡ Activity detected. Resetting inactivity timer.");
    const now = moment();
    lastActivityRef.current = now;
    localStorage.setItem("last_active", now.toString());

    if (showWarning) {
      console.log("[AppLogout] 🔔 Clearing warning state.");
      setShowWarning(false);
    }
  }, [isAuthPage, showWarning, token]);

  // 1. Token Switch Listener & Initial Reset
  useEffect(() => {
    console.log("[AppLogout] 🔄 Context initialized. Token presence:", !!token);
    const checkToken = () => {
      const currentToken =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (currentToken !== token) {
        console.log("[AppLogout] 🔑 Token change detected between state and storage.");
        setToken(currentToken);
      }
    };

    if (token && !isAuthPage()) {
      console.log("[AppLogout] 🚀 Active session detected. Initializing timer.");
      const now = moment();
      localStorage.setItem("last_active", now.toString());
      lastActivityRef.current = now;
      setRemainingTime(INACTIVITY_LIMIT);
    }

    const interval = setInterval(checkToken, 2000); // Check for token changes every 2s
    return () => clearInterval(interval);
  }, [token, isAuthPage]);

  // 2. Activity Monitoring
  useEffect(() => {
    if (!token || isAuthPage() || isLoggingOutRef.current) {
      console.log("[AppLogout] 🛑 Monitoring suspended (Auth page or no token)");
      return;
    }

    console.log("[AppLogout] 👁️ Starting activity monitoring.");
    const events = [
      "mousemove",
      "mousedown",
      "click",
      "keypress",
      "keydown",
      "keyup",
      "scroll",
      "touchstart",
      "wheel",
    ];
    const handleEvent = () => resetActivity();

    events.forEach((event) =>
      window.addEventListener(event, handleEvent, { passive: true }),
    );
    return () => {
      console.log("[AppLogout] 🛑 Stopping activity monitoring.");
      events.forEach((event) => window.removeEventListener(event, handleEvent));
    };
  }, [token, isAuthPage, resetActivity]);

  // 3. Multi-tab Sync
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "last_active" && e.newValue) {
        console.log("[AppLogout] 🌐 Multi-tab sync: External activity detected.");
        // lastActivityRef.current = Number(e.newValue);
      }
      if (e.key === "token") {
        console.log("[AppLogout] 🌐 Multi-tab sync: Token change detected.");
        setToken(e.newValue);
        if (!e.newValue && !isAuthPage()) {
          console.log("[AppLogout] 🌐 Multi-tab sync: Token removed elsewhere, redirecting.");
          window.location.href = "/#/";
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [isAuthPage]);

  // 4. Main Timer Interval
  useEffect(() => {
    if (!token || isAuthPage() || isLoggingOutRef.current) {
      setRemainingTime(INACTIVITY_LIMIT);
      return;
    }

    console.log("[AppLogout] ⏲️ Timer interval started.");
    const interval = setInterval(() => {
      // Double check token inside interval
      if (!token || isAuthPage() || isLoggingOutRef.current) {
        clearInterval(interval);
        setRemainingTime(INACTIVITY_LIMIT); // Reset timer if token disappears
        return;
      }

      const now = moment();
      const lastActive =
        localStorage.getItem("last_active") || lastActivityRef.current;
      const logoutTime = moment(lastActive).add(LOGOUT_TIME_LIMIT, "minutes");
      
      const remaining = logoutTime.diff(now, "seconds");
      setRemainingTime(remaining);

      // Log only every 30 seconds to avoid spamming, but log critical points
      if (remaining % 30 === 0 || remaining <= 30) {
        console.log(`[AppLogout] ⏳ Remaining: ${remaining}s`);
      }

      if (remaining <= 0) {
        console.warn("[AppLogout] ⏰ Inactivity limit reached. Logging out.");
        logoutAction();
      } else if (remaining <= (WARNING_TIME / 1000)) {
        if (!showWarning) {
          console.log(`[AppLogout] ⚠️ Approaching limit: ${remaining}s left.`);
          setShowWarning(true);
        }
      } else {
        if (showWarning) setShowWarning(false);
      }
    }, CHECK_INTERVAL);

    return () => {
      console.log("[AppLogout] ⏲️ Timer interval cleared.");
      clearInterval(interval);
    };
  }, [token, isAuthPage, logoutAction, showWarning]);

  // 5. Warning Toast
  useEffect(() => {
    if (token && showWarning && !isAuthPage() && !isLoggingOutRef.current) {
      const seconds = Math.ceil(remainingTime);
      if ([10, 5, 2].includes(seconds)) {
        console.log(`[AppLogout] 🔔 Warning toast shown: ${seconds}s`);
        // Toast(`Session expires in ${seconds}s`, "warning");
      }
    }
  }, [token, showWarning, remainingTime, isAuthPage]);

  return children;
};

export default AppLogout;
